import { BlockNoteView } from '@blocknote/mantine';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import {
  WorksheetAnswerDetail,
  WorksheetParticipant,
  useGetWorksheetAnswerByUserAndWorksheet,
  useGetWorksheetParticipants,
} from 'hooks/api/backend/worksheets/useWorksheets';
import React, { ReactElement, useState, useMemo, useEffect } from 'react';
import { createEditorWithMode } from 'utils/createEditorWithMode';
import '@blocknote/mantine/style.css';
import 'styles/worksheets/styles.css';
import { EducationIcon, UserIcon } from 'assets/icons/v2';
import { CustomBlockContentSpec, schema } from 'pages/in-app/worksheet/Worksheet';
import { Filters } from 'components/common/Table/Filters';
import { useGetClassesByWorksheet } from 'hooks/api/backend/managament/getClasses';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { useSearchParams } from 'react-router-dom';

type Props = {
  worksheetId: string | undefined;
  initialClassId?: string;
  initialParticipantId?: string;
  participantWorksheetData?: WorksheetAnswerDetail | null;
};

export const WorksheetAnswer = ({
  worksheetId,
  initialClassId,
  initialParticipantId,
  participantWorksheetData,
}: Props): ReactElement | null => {
  const [searchParams, setSearchParams] = useSearchParams();
  const classIdParam = searchParams.get('classId') ?? '';

  const [initialContent, setInitialContent] = useState<CustomBlockContentSpec[] | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string>(initialClassId ?? classIdParam);
  const [selectedParticipant, setSelectedParticipant] = useState<WorksheetParticipant | null>(null);
  const enableUserView = !!participantWorksheetData;
  const { isB2BTeacherOrAdmin } = useIsB2BTeacherOrAdmin();
  const canUseClassFilter = !enableUserView && isB2BTeacherOrAdmin === true;

  const { data: classesByWorksheet } = useGetClassesByWorksheet(
    !enableUserView ? worksheetId || '' : ''
  );

  const classOptions = useMemo(
    () =>
      classesByWorksheet?.classes
        .filter((item) => item.isConnectedToWorksheet)
        .map((item) => ({
          id: item.id,
          label: item.name,
        })) ?? [],
    [classesByWorksheet]
  );

  const { data: participants = [] } = useGetWorksheetParticipants(
    !enableUserView ? worksheetId : undefined,
    canUseClassFilter ? selectedClassId || undefined : undefined
  );

  const { data: dbWorksheetData } = useGetWorksheetAnswerByUserAndWorksheet(
    !enableUserView ? worksheetId : undefined,
    selectedParticipant?.userData.id
  );

  const worksheetData = useMemo(() => {
    if (dbWorksheetData) return dbWorksheetData;
    if (participantWorksheetData) return participantWorksheetData;
    return null;
  }, [dbWorksheetData, participantWorksheetData]);

  const editor = useMemo(() => {
    if (initialContent === null || initialContent.length === 0)
      return createEditorWithMode('review', { schema });
    return createEditorWithMode('review', { schema, initialContent });
  }, [initialContent]);

  // Load initial content from the worksheet data or local storage to editor
  useEffect(() => {
    const loadContent = async (): Promise<void> => {
      if (!worksheetData) return;

      const worksheetContent = worksheetData.content;
      if (worksheetContent && worksheetContent.length > 0) {
        setInitialContent(worksheetContent);
        return;
      }

      setInitialContent([]);
    };

    loadContent();
  }, [worksheetData]);

  useEffect(() => {
    if (!participants.length) {
      setSelectedParticipant(null);
      return;
    }

    const currentParticipant = selectedParticipant
      ? participants.find(
          (participant) => participant.userData.id === selectedParticipant.userData.id
        )
      : null;

    if (!currentParticipant) {
      setSelectedParticipant(participants[0]);
      setInitialContent(null);
    }
  }, [participants, selectedParticipant]);

  useEffect(() => {
    if (canUseClassFilter) return;
    if (!selectedClassId) return;
    setSelectedClassId('');
  }, [canUseClassFilter, selectedClassId]);

  useEffect(() => {
    if (!canUseClassFilter) return;
    setSelectedClassId(classIdParam);
  }, [classIdParam, canUseClassFilter]);

  useEffect(() => {
    if (!initialClassId) return;
    if (!canUseClassFilter) return;
    setSelectedClassId((current) => current || initialClassId);
  }, [initialClassId, canUseClassFilter]);

  useEffect(() => {
    if (!canUseClassFilter) return;
    if (!classIdParam) return;
    if (classOptions.length === 0) return;
    const hasSelectedClass = classOptions.some((option) => option.id === classIdParam);
    if (!hasSelectedClass) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('classId');
      setSearchParams(nextParams);
      setSelectedParticipant(null);
      setInitialContent(null);
    }
  }, [canUseClassFilter, classIdParam, classOptions, searchParams, setSearchParams]);

  useEffect(() => {
    if (!initialParticipantId || !participants.length) return;
    const match = participants.find((p) => p.userData.id === initialParticipantId);
    if (match) setSelectedParticipant(match);
  }, [initialParticipantId, participants]);

  const handleClassChange = (classId: string): void => {
    if (classId === classIdParam) return;
    setSelectedParticipant(null);
    setInitialContent(null);

    const nextParams = new URLSearchParams(searchParams);
    if (classId) {
      nextParams.set('classId', classId);
    } else {
      nextParams.delete('classId');
    }
    setSearchParams(nextParams);
  };

  const handleParticipantChange = (id: string): void => {
    const participant = participants.find(({ userData }) => userData.id === id);
    if (selectedParticipant?.userData.id === id) return;
    if (participant) {
      setSelectedParticipant(participant);
      setInitialContent(null);
    }
  };

  const title = enableUserView ? 'Meine Antwort' : 'Antworten der Auszubildenden';
  const showNoAnswersForClass =
    !enableUserView && selectedClassId !== '' && participants.length === 0;
  return (
    <VStack width="100%" align="stretch" gap="25px" py="l">
      <Text textStyle="h4.medium">{title}</Text>
      {!enableUserView && (
        <HStack>
          {canUseClassFilter && (
            <Filters
              filters={[
                [
                  {
                    id: 'class',
                    placeholder: 'Alle Teilnehmenden',
                    value: selectedClassId,
                    options: classOptions,
                    onChange: handleClassChange,
                    icon: <EducationIcon color="icon.accent" />,
                    showPlaceholderAsFirstOption: true,
                    disabled: classOptions.length === 0,
                  },
                ],
              ]}
            />
          )}
          <Filters
            filters={[
              [
                {
                  id: 'person',
                  placeholder: 'Person auswählen',
                  value: selectedParticipant?.userData.id || '',

                  options: participants.map(({ userData }) => ({
                    id: userData.id,
                    label: `${userData.firstName} ${userData.lastName}`,
                  })),
                  disabled: participants.length <= 1,
                  onChange: handleParticipantChange,
                  icon: <UserIcon color="icon.accent" boxSize="16px" />,
                  hidePlaceholder: participants.length !== 0,
                },
              ],
            ]}
          />
        </HStack>
      )}
      {showNoAnswersForClass ? (
        <Box
          bg="background.primary"
          flexGrow={1}
          height="100%"
          width="100%"
          maxWidth="100%"
          borderRadius={{ md: '2xl' }}
          py="4xl"
        >
          <VStack gap="10px" align="center" justify="center" width="100%">
            <Text textStyle="body.m.medium" color="text.tertiary" textAlign="center">
              Für diesen Kurs liegen noch keine Antworten vor.
            </Text>
            <Text textStyle="body.s.regular" color="text.tertiary" textAlign="center">
              Wähle einen anderen Kurs oder warte, bis Antworten vorliegen.
            </Text>
          </VStack>
        </Box>
      ) : (
        <Box
          className="test-my"
          bg="background.primary"
          flexGrow={1}
          height="100%"
          width="100%"
          maxWidth="100%"
          borderRadius={{ md: '2xl' }}
        >
          {!enableUserView && selectedParticipant && !worksheetData ? (
            <VStack gap="10px" align="center" justify="center" width="100%" py="4xl">
              <Text textStyle="body.m.medium" color="text.tertiary" textAlign="center">
                Für diese Person liegt noch keine Antwort vor.
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary" textAlign="center">
                Wähle eine andere Person oder warte, bis eine Antwort gespeichert wird.
              </Text>
            </VStack>
          ) : null}
          {editor && worksheetData && (
            <BlockNoteView
              slashMenu={false}
              editable={false}
              editor={editor}
              data-theming-css-variables
              emojiPicker={false}
            />
          )}
        </Box>
      )}
    </VStack>
  );
};
